import React, {
  useCallback,
  useContext,
  useReducer,
  useState,
  useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelectedTheme, useTranslate } from '../../../Hooks';
import { PageTitle } from '../../../Components';
import { CustomBreadCrumb, CustomButton } from '../../../Components/V2';
import CompletePropertyProfileContextProvider, {
  CompletePropertyProfileContext,
} from '../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext';
import { CompletePropertyProfileSteps } from './CompletePropertyProfileSteps/CompletePropertyProfileSteps';
import { getDownloadableLink, GetParams } from '../../../Helper';

import useStyles from './styles';
import { Box } from '@material-ui/core';
import { useVerticalNav } from '../../../Contexts/VerticalNavContext';
import { EmptyUnitGalleryIcon, MarkerPinIcon01 } from '../../../assets/icons';

function CompletePropertyProfileContent() {
  const styles = useStyles();
  const history = useHistory();
  const propertyId = +GetParams('id');
  const { setAlertBoxContent } = useVerticalNav();
  const context = useContext(CompletePropertyProfileContext);
  const propertyInfo = context?.propertyInfo;
  const propertyImage = context?.propertyImage;
  const { translate } = useTranslate('CompletePropertyProfile');
  const completePropertyProfileSteps = CompletePropertyProfileSteps;
  const {
    theme: { palette },
  } = useSelectedTheme();
  const reducer = useCallback((state, action) => {
    if (action.id !== 'edit') return { ...state, [action.id]: action.value };
    return {
      ...action.value,
    };
  }, []);
  const [stepsStatus, setStepsStatus] = useState({});
  const [isNavigating, setIsNavigating] = useState(false);
  const [activeStep, setActiveStep] = useReducer(reducer, {
    id: 1,
    completionPercentage: 0,
  });

  const calculateCompletionPercentage = useCallback(() => {
    let totalPercentage = 0;
    Object.values(completePropertyProfileSteps).forEach((step) => {
      if (stepsStatus[step.id]?.isPassed) {
        totalPercentage += step.percentage;
      }
    });
    return totalPercentage;
  }, [stepsStatus]);

  const onStepChange = useCallback(
    (stepId, skipValidation = false) => {
      if (!skipValidation && context?.callValidationHandler) {
        const canNavigate = context.callValidationHandler();
        if (!canNavigate) {
          return;
        }
      }

      setActiveStep({
        id: 'edit',
        value: {
          id: stepId,
          completionPercentage: calculateCompletionPercentage(),
        },
      });
    },
    [context, calculateCompletionPercentage]
  );

  const handleNextStep = useCallback(async () => {
    if (isNavigating || context?.isSaving) return;

    try {
      setIsNavigating(true);

      const currentStepKeys = Object.keys(completePropertyProfileSteps);
      const currentIndex = currentStepKeys.findIndex(
        (key) => completePropertyProfileSteps[key].id === activeStep.id
      );

      if (context?.callNextHandler) {
        const result = await context.callNextHandler();

        if (result && result.success) {
          setStepsStatus((prev) => ({
            ...prev,
            [activeStep.id]: { isPassed: true },
          }));

          if (currentIndex < currentStepKeys.length - 1) {
            const nextStepKey = currentStepKeys[currentIndex + 1];
            const nextStepId = completePropertyProfileSteps[nextStepKey].id;

            setTimeout(() => {
              onStepChange(nextStepId, true);
              setIsNavigating(false);
            }, 100);
          } else {
            setAlertBoxContent({
              display: true,
              variant: 'success',
              title: translate('all-steps-completed-successfully'),
              onClose: () => {
                setAlertBoxContent(null);
                history.push('/home/Properties-CRM/view');
              },
            });
            setTimeout(() => {
              history.push('/home/Properties-CRM/view');
            }, 2000);
            setIsNavigating(false);
          }
        } else {
          setIsNavigating(false);
        }
      }
    } catch (error) {
      setIsNavigating(false);
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: translate('error-processing-step'),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
  }, [
    activeStep.id,
    context,
    onStepChange,
    setAlertBoxContent,
    translate,
    history,
    isNavigating,
  ]);

  const handleSaveAndExit = useCallback(async () => {
    if (isNavigating || context?.isSaving) return;

    setIsNavigating(true);

    if (context?.callSaveHandler) {
      const result = await context.callSaveHandler();
      if (result && result.success) {
        setStepsStatus((prev) => ({
          ...prev,
          [activeStep.id]: { isPassed: true },
        }));

        setAlertBoxContent({
          display: true,
          variant: 'success',
          title: translate('property-saved-successfully'),
          onClose: () => {
            setAlertBoxContent(null);
            history.push('/home/Properties-CRM/view');
          },
        });

        setTimeout(() => {
          history.push('/home/Properties-CRM/view');
        }, 2000);
      } else {
        setIsNavigating(false);
      }
    } else {
      setIsNavigating(false);
    }
  }, [
    activeStep.id,
    context,
    history,
    setAlertBoxContent,
    translate,
    isNavigating,
  ]);

  useEffect(() => {
    if (context?.setNavigationHandler) {
      context.setNavigationHandler(handleNextStep);
    }
  }, [context, handleNextStep]);

  useEffect(() => {
    const newPercentage = calculateCompletionPercentage();
    if (newPercentage !== activeStep.completionPercentage) {
      setActiveStep({
        id: 'completionPercentage',
        value: newPercentage,
      });
    }
  }, [
    stepsStatus,
    calculateCompletionPercentage,
    activeStep.completionPercentage,
  ]);

  const getComponentByStepId = () => {
    const step = Object.values(completePropertyProfileSteps).find(
      (step) => step.id === activeStep.id
    );
    const StepComponent = step?.component;
    return StepComponent ? (
      <StepComponent
        propertyId={propertyId}
        stepId={activeStep.id}
        onStepComplete={() => {
          setStepsStatus((prev) => ({
            ...prev,
            [activeStep.id]: { isPassed: true },
          }));
        }}
      />
    ) : null;
  };

  const getIconByStepId = (stepId) => {
    const step = Object.values(completePropertyProfileSteps).find(
      (step) => step.id === stepId
    );
    return step?.Icon || null;
  };

  const BreadcrumbSkeleton = () => (
    <div className={styles.breadcrumbSkeleton}>
      <div
        className={`${styles.skeletonBox} ${styles.skeletonPulse}`}
        style={{ width: '40px', height: '20px' }}
      />
      <span>/</span>
      <div
        className={`${styles.skeletonBox} ${styles.skeletonPulse}`}
        style={{ width: '80px', height: '20px' }}
      />
      <span>/</span>
      <div
        className={`${styles.skeletonBox} ${styles.skeletonPulse}`}
        style={{ width: '120px', height: '20px' }}
      />
    </div>
  );

  const PropertyMiniPreviewSkeleton = () => (
    <div className={styles.propertyMiniPreviewWrapper}>
      <div className={styles.propertyMiniPreviewSkeleton}>
        <div
          className={`${styles.propertyImageSkeleton} ${styles.skeletonPulse}`}
        />
        <div className={styles.propertyInfoSkeleton}>
          <div
            className={`${styles.skeletonBox} ${styles.skeletonPulse}`}
            style={{ width: '160px', height: '14px' }}
          />
          <div
            className={`${styles.skeletonBox} ${styles.skeletonPulse}`}
            style={{ width: '120px', height: '12px' }}
          />
          <div
            className={`${styles.skeletonBox} ${styles.skeletonPulse}`}
            style={{ width: '140px', height: '12px' }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.completePropertyProfilePageWrapper}>
        {!context?.isFetched ? (
          <BreadcrumbSkeleton />
        ) : (
          <CustomBreadCrumb
            breadcrumbs={[
              { label: translate('crm'), nonClickable: true },
              {
                label: translate('properties'),
                link: '/home/Properties-CRM/view',
              },
              {
                label: (
                  <>
                    <Box className={styles.breadcrumbActiveProperty}>
                      {propertyInfo?.property_name || 'N/A'}
                    </Box>
                  </>
                ),
                link: '/home/Properties-CRM/view',
              },
            ]}
          />
        )}
        <PageTitle
          title={
            <Box className={styles.titleWithPercentage}>
              {translate('complete-property-profile')}
              <Box className={styles.completionPercentage}>
                {activeStep.completionPercentage}%
              </Box>
            </Box>
          }
          subTitle={translate('complete-profile-sub-title')}
          hideDivider={true}
          showBackIcon={true}
        >
          <CustomButton
            className={styles.saveActionButton}
            disabled={context?.isSaving || isNavigating}
            onClick={handleSaveAndExit}
          >
            {translate('save-&-exit')}
          </CustomButton>
          <CustomButton
            className={styles.nextActionButton}
            disabled={context?.isSaving || isNavigating}
            onClick={handleNextStep}
          >
            {translate('next')}
          </CustomButton>
        </PageTitle>
        <div className={styles.completePropertyProfileContentWrapper}>
          <div className={styles.stepsContainer}>
            {Object.values(completePropertyProfileSteps).map((step) => (
              <div
                key={step.id}
                onClick={() => !isNavigating && onStepChange(step.id)}
                className={`${styles.stepItem} ${
                  activeStep.id === step.id ? styles.activeStep : ''
                } ${
                  stepsStatus[step.id]?.isPassed ? styles.completedStep : ''
                }`}
              >
                <div className={styles.stepContainer}>
                  {getIconByStepId(step.id)}
                  <span className={styles.stepName}>
                    {translate(step.title)}
                  </span>
                  {stepsStatus[step.id]?.isPassed && (
                    <span className={styles.checkmark}>✓</span>
                  )}
                </div>
              </div>
            ))}
            {!context?.isFetched ? (
              <PropertyMiniPreviewSkeleton />
            ) : (
              <div className={styles.propertyMiniPreviewWrapper}>
                <div className={styles.propertyMiniPreview}>
                  <div className={styles.propertyImageContainer}>
                    {propertyImage?.fileId ? (
                      <img
                        alt={`Image ${propertyImage?.fileName}`}
                        src={
                          propertyImage?.fileId
                            ? getDownloadableLink(propertyImage.fileId)
                            : ''
                        }
                      />
                    ) : (
                      <EmptyUnitGalleryIcon fill={palette.foreground.quinary} />
                    )}
                  </div>
                  <div className={styles.propertyMiniPreviewInfo}>
                    <span>{propertyInfo?.property_name || 'N/A'}</span>
                    <span>
                      {propertyInfo?.property_type?.lookupItemName || 'N/A'}
                    </span>
                    <span>
                      <MarkerPinIcon01
                        width='12'
                        height='12'
                        fill={'#98A2B3'}
                      />
                      {propertyInfo?.country?.lookupItemName || 'N/A'} -{' '}
                      {propertyInfo?.city?.lookupItemName || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.stepPageContainer}>
            {getComponentByStepId(activeStep?.id)}
          </div>
        </div>
      </div>
    </>
  );
}

function CompletePropertyProfile() {
  return (
    <CompletePropertyProfileContextProvider>
      <CompletePropertyProfileContent />
    </CompletePropertyProfileContextProvider>
  );
}

export default CompletePropertyProfile;
